import React, { useState, useEffect, useMemo, ChangeEvent } from 'react';
import { AiOutlineMenu, AiOutlineClose } from 'react-icons/ai';
import { BsPalette } from 'react-icons/bs';
import { FaGlobe } from 'react-icons/fa';
import { useTheme } from '../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';

const Navbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  const [now, setNow] = useState(new Date());
  const { theme, toggleTheme } = useTheme();
  const { t, i18n } = useTranslation();
  const isModern = theme === 'modern';

  const availableLanguages = useMemo(() => ['en', 'ar', 'ja', 'zh', 'fr', 'es'], []);
  const currentLanguage = useMemo(() => {
    const code = i18n.resolvedLanguage || i18n.language || 'en';
    return code.split('-')[0];
  }, [i18n.language, i18n.resolvedLanguage]);

  const navLinks = useMemo(
    () => [
      { id: 'hero',         href: '#hero',         label: t('navbar.links.home') },
      { id: 'about',        href: '#about',        label: t('navbar.links.about') },
      { id: 'skills',       href: '#skills',       label: t('navbar.links.skills') },
      { id: 'resume',       href: '#resume',       label: t('navbar.links.resume') },
      { id: 'projects',     href: '#projects',     label: t('navbar.links.projects') },
      // { id: 'certificates', href: '#certificates', label: t('navbar.links.certificates') },
      { id: 'contact',      href: '#contact',      label: t('navbar.links.contact') },
    ],
    [t]
  );

  // Scroll handler — scrollspy + collapsed state
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // active section detection
      const offsets = navLinks.map((link) => {
        const el = document.getElementById(link.id);
        if (!el) return { id: link.id, top: Number.POSITIVE_INFINITY };
        const rect = el.getBoundingClientRect();
        return { id: link.id, top: rect.top };
      });
      // pick the section whose top is closest above the viewport center
      const center = window.innerHeight * 0.35;
      const candidate = offsets
        .filter((o) => o.top <= center)
        .sort((a, b) => b.top - a.top)[0];
      if (candidate) setActiveSection(candidate.id);
      else setActiveSection('hero');
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [navLinks]);

  // Live clock for modern theme
  useEffect(() => {
    if (!isModern) return;
    const id = window.setInterval(() => setNow(new Date()), 1000);
    return () => window.clearInterval(id);
  }, [isModern]);

  const handleLanguageChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const newLanguage = event.target.value;
    i18n.changeLanguage(newLanguage);
    localStorage.setItem('i18nextLng', newLanguage);
  };

  const formatTime = (d: Date) =>
    `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}:${String(d.getUTCSeconds()).padStart(2, '0')}`;

  const navWrapperClass = isModern ? 'modern-nav nav-frame--sf' : 'nav-frame--nb';

  return (
    <nav
      className={`fixed top-4 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'py-2' : 'py-4'}`}
    >
      <div className="max-w-7xl mx-auto px-4">
        <div className={`${navWrapperClass} relative`}>
          {/* ============== LEFT EDGE DECOR ============== */}
          {isModern ? (
            <span className="sf-nav-edge sf-nav-edge--left" aria-hidden="true"></span>
          ) : (
            <span className="nb-nav-edge nb-nav-edge--left" aria-hidden="true"></span>
          )}

          <div className="flex items-center justify-between gap-3 px-4 sm:px-6 py-3 sm:py-4 relative">
            {/* ============== BRAND ============== */}
            <a href="#hero" className={isModern ? 'sf-brand' : 'nb-brand'} aria-label="Home">
              {isModern ? (
                <>
                  <span className="sf-brand-mark" aria-hidden="true">
                    <span className="sf-brand-mark-letter">M</span>
                  </span>
                  <span className="sf-brand-text">
                    <span className="sf-brand-text-name">MOTI</span>
                    <span className="sf-brand-text-meta">.SYS // v4.7</span>
                  </span>
                </>
              ) : (
                <>
                  <span className="nb-brand-mark" aria-hidden="true">M</span>
                  <span className="nb-brand-text">
                    <span className="nb-brand-text-name">★ MOTI</span>
                    <span className="nb-brand-text-meta">portfolio · zine</span>
                  </span>
                </>
              )}
            </a>

            {/* ============== DESKTOP MENU ============== */}
            <div className="hidden md:flex items-center gap-2 lg:gap-3">
              <ul className={isModern ? 'sf-nav-list' : 'nb-nav-list'}>
                {navLinks.map((link, idx) => {
                  const isActive = activeSection === link.id;
                  if (isModern) {
                    return (
                      <li key={link.href}>
                        <a
                          href={link.href}
                          className={`sf-nav-link ${isActive ? 'sf-nav-link--active' : ''}`}
                          aria-current={isActive ? 'page' : undefined}
                        >
                          <span className="sf-nav-link-num">{String(idx + 1).padStart(2, '0')}</span>
                          <span className="sf-nav-link-label">{link.label}</span>
                          <span className="sf-nav-link-bar" aria-hidden="true"></span>
                        </a>
                      </li>
                    );
                  }
                  return (
                    <li key={link.href}>
                      <a
                        href={link.href}
                        className={`nb-nav-link nb-nav-link--${idx % 4} ${isActive ? 'nb-nav-link--active' : ''}`}
                        aria-current={isActive ? 'page' : undefined}
                      >
                        {link.label}
                      </a>
                    </li>
                  );
                })}
              </ul>

              {/* Language switcher */}
              <div className={isModern ? 'sf-lang' : 'nb-lang'}>
                <FaGlobe aria-hidden="true" className={isModern ? 'sf-lang-icon' : 'nb-lang-icon'} />
                <label htmlFor="language-switcher" className="sr-only">
                  {t('navbar.language.ariaLabel')}
                </label>
                <select
                  id="language-switcher"
                  value={currentLanguage}
                  onChange={handleLanguageChange}
                  aria-label={t('navbar.language.ariaLabel')}
                >
                  {availableLanguages.map((lng) => (
                    <option key={lng} value={lng}>{t(`languages.${lng}`)}</option>
                  ))}
                </select>
              </div>

              {/* Theme toggle */}
              <button
                onClick={toggleTheme}
                className={isModern ? 'sf-theme-switch' : 'nb-theme-switch'}
                title={t('navbar.themeToggle.switchTo', {
                  theme: t(theme === 'neobrutalism' ? 'theme.modern' : 'theme.classic')
                })}
                aria-label={t('navbar.themeToggle.switchTo', {
                  theme: t(theme === 'neobrutalism' ? 'theme.modern' : 'theme.classic')
                })}
              >
                <BsPalette size={14} />
                <span className="sf-theme-switch-track" aria-hidden="true">
                  <span className="sf-theme-switch-knob"></span>
                </span>
                <span className="sf-theme-switch-label">
                  {t(theme === 'neobrutalism' ? 'theme.modern' : 'theme.classic')}
                </span>
              </button>

              {/* Live clock — modern only */}
              {isModern && (
                <div className="sf-clock" aria-hidden="true">
                  <span className="sf-clock-pulse"></span>
                  <span className="sf-clock-time">{formatTime(now)}</span>
                  <span className="sf-clock-zone">UTC</span>
                </div>
              )}
            </div>

            {/* ============== MOBILE TOGGLE ============== */}
            <button
              className={isModern ? 'sf-menu-toggle md:hidden' : 'nb-menu-toggle md:hidden'}
              onClick={() => setMenuOpen(!menuOpen)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
            >
              {menuOpen ? <AiOutlineClose size={22} /> : <AiOutlineMenu size={22} />}
            </button>
          </div>

          {/* ============== RIGHT EDGE DECOR ============== */}
          {isModern && (
            <span className="sf-nav-edge sf-nav-edge--right" aria-hidden="true"></span>
          )}

          {/* ============== MOBILE MENU ============== */}
          {menuOpen && (
            <div className={isModern ? 'sf-mobile-menu md:hidden' : 'nb-mobile-menu md:hidden'}>
              <div className="max-h-[calc(100vh-120px)] overflow-y-auto overscroll-contain">
                <ul className={isModern ? 'sf-mobile-list' : 'nb-mobile-list'}>
                  {navLinks.map((link, idx) => {
                    const isActive = activeSection === link.id;
                    return (
                      <li key={link.href}>
                        <a
                          href={link.href}
                          className={
                            isModern
                              ? `sf-mobile-link ${isActive ? 'sf-mobile-link--active' : ''}`
                              : `nb-mobile-link nb-mobile-link--${idx % 4} ${isActive ? 'nb-mobile-link--active' : ''}`
                          }
                          onClick={() => setMenuOpen(false)}
                        >
                          {isModern && <span className="sf-nav-link-num">{String(idx + 1).padStart(2, '0')}</span>}
                          <span>{link.label}</span>
                          {isActive && <span className={isModern ? 'sf-active-pill' : 'nb-active-pill'}>NOW</span>}
                        </a>
                      </li>
                    );
                  })}
                </ul>

                <div className={isModern ? 'sf-mobile-controls' : 'nb-mobile-controls'}>
                  <div className={isModern ? 'sf-lang sf-lang--full' : 'nb-lang nb-lang--full'}>
                    <FaGlobe aria-hidden="true" className={isModern ? 'sf-lang-icon' : 'nb-lang-icon'} />
                    <select
                      id="mobile-language-switcher"
                      value={currentLanguage}
                      onChange={(e) => { handleLanguageChange(e); setMenuOpen(false); }}
                      aria-label={t('navbar.language.ariaLabel')}
                    >
                      {availableLanguages.map((lng) => (
                        <option key={lng} value={lng}>{t(`languages.${lng}`)}</option>
                      ))}
                    </select>
                  </div>
                  <button
                    onClick={toggleTheme}
                    className={isModern ? 'sf-theme-switch sf-theme-switch--full' : 'nb-theme-switch nb-theme-switch--full'}
                  >
                    <BsPalette size={14} />
                    <span className="sf-theme-switch-track" aria-hidden="true">
                      <span className="sf-theme-switch-knob"></span>
                    </span>
                    <span className="sf-theme-switch-label">
                      {t('navbar.themeToggle.switchTo', {
                        theme: t(theme === 'neobrutalism' ? 'theme.modern' : 'theme.classic')
                      })}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
